'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { IconAdd } from '@repo/svgs';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { QuestionCard } from './QuestionCard';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { getBuildingQA, type Building, type SeoQuestion } from '@repo/api';
import dynamic from 'next/dynamic';
import { cn } from '@/lib/cn';
import { useAnalytics } from '@/hooks/useAnalytics';

// Lazy load modal components
const AskQuestionModal = dynamic(() =>
  import('./modals/AskQuestionModal').then(mod => mod.AskQuestionModal),
);

const AllRentersQnAModal = dynamic(() =>
  import('./modals/AllRentersQnAModal').then(mod => mod.AllRentersQnAModal),
);

interface BuildingRenterQnAProps {
  building: Building;
}

export function BuildingRenterQnA({ building }: BuildingRenterQnAProps) {
  // `building.seoQuestions` may be `null` depending on API/SSR payload; normalize to an array.
  const rawSeoQuestions = (building as { seoQuestions?: unknown })?.seoQuestions;
  const seoQuestions: SeoQuestion[] = useMemo(
    () => (Array.isArray(rawSeoQuestions) ? (rawSeoQuestions as SeoQuestion[]) : []),
    [rawSeoQuestions],
  );

  const [questions, setQuestions] = useState<SeoQuestion[]>(seoQuestions);
  const analytics = useAnalytics();
  const screen = 'building' as const;

  // Keep SSR questions as the initial render, and reset when navigating between buildings.
  useEffect(() => {
    setQuestions(seoQuestions);
  }, [building?.slug, seoQuestions]);

  // Refresh with live data on the client (only depends on the building slug).
  useEffect(() => {
    let cancelled = false;

    const fetchQuestions = async () => {
      if (!building?.slug) return;
      try {
        const data = await getBuildingQA(building.slug);
        const nextQuestions: SeoQuestion[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? (data.results as SeoQuestion[])
            : [];
        if (cancelled) return;
        setQuestions(nextQuestions);
      } catch {
        // best-effort; keep SSR questions if fetch fails
      }
    };

    void fetchQuestions();

    return () => {
      cancelled = true;
    };
  }, []);

  const safeQuestions: SeoQuestion[] = Array.isArray(questions) ? questions : [];
  const hasQuestions = safeQuestions.length > 0;
  // Display first 2 questions
  const displayQuestions = safeQuestions.slice(0, 2);
  const totalQuestions = safeQuestions.length;

  if (!hasQuestions) {
    // Empty state
    return (
      <div className="w-full">
        {/* Section Header */}
        <SectionHeader title="Renter Q&A" />

        {/* Empty State Content */}
        <div className={cn('space-y-4', 'px-2', 'md:px-4')}>
          <p className="text-body-2 text-secondary w-full">Be the first to ask a question!</p>
          <ModalTrigger label="Ask a question" content={<AskQuestionModal />} urlKey="ask-question">
            <Button
              variant="secondary"
              className="px-6 py-4"
              onClick={() => {
                analytics.track(`${screen}_ask_question`);
              }}
            >
              <div className="flex items-center gap-2">
                <IconAdd className="w-4 h-4 icon-accent" />
                Ask a question
              </div>
            </Button>
          </ModalTrigger>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Section Header with Ask Question Button */}
      <div className={cn('flex items-center justify-between', 'py-2')}>
        <SectionHeader title="Renter Q&A" />
        <ModalTrigger label="Ask a question" content={<AskQuestionModal />} urlKey="ask-question">
          <Button
            variant="secondary"
            size="sm"
            className="px-4 py-3"
            onClick={() => {
              analytics.track(`${screen}_ask_question`);
            }}
          >
            <div className="flex items-center gap-2">
              <IconAdd className="w-4 h-4 icon-accent" />
              Ask a question
            </div>
          </Button>
        </ModalTrigger>
      </div>

      {/* Questions List */}
      <div className="flex flex-col gap-2 w-full md:px-2">
        {displayQuestions.map(question => (
          <QuestionCard
            key={question.id}
            question={question}
            enableModalTrigger={true}
            eventScreen={screen}
          />
        ))}

        {/* View All Questions Button */}
        {totalQuestions > 2 && (
          <div className={cn('px-2', 'md:px-4')}>
            <ModalTrigger
              label="Renter Q&A"
              content={<AllRentersQnAModal />}
              urlKey="all-renters-qna"
              onOpen={() => analytics.track(`${screen}_seeall_question_answer`)}
            >
              <Button variant="secondary" className="px-6 py-4">
                View all {totalQuestions} questions
              </Button>
            </ModalTrigger>
          </div>
        )}
      </div>
    </div>
  );
}

export default BuildingRenterQnA;
